import { Fragment, useEffect, useState } from 'react';
import { endpoints, type CapitalSettings } from '../api';
import { Button, EmptyState, ErrorState, Field, LoadingState, Note, Page, Section, Stat, Stats, Status, currency, dateTime, number } from '../components';
import { useApiQuery } from '../hooks';
import { FinanceHistory } from './finance-history';
import { TypeSearch } from '../type-search';

export function FinancePage() {
  const summary = useApiQuery(endpoints.financeSummary);
  const records = useApiQuery(endpoints.financeRecords);
  const accounting = useApiQuery(endpoints.accounting);
  const settings = useApiQuery(endpoints.capitalSettings);
  const [capital, setCapital] = useState<CapitalSettings | null>(null);
  const [recordBusy, setRecordBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone: 'success' | 'error' } | null>(null);
  useEffect(() => { if (settings.data) setCapital(settings.data); }, [settings.data]);

  async function addRecord(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setRecordBusy(true); setMessage(null);
    try {
      await endpoints.addFinanceRecord({
        date: String(form.get('date')),
        type: String(form.get('type')) as 'income' | 'expense',
        amount: Number(form.get('amount')),
        category: String(form.get('category')),
        description: String(form.get('description')),
      });
      formElement.reset();
      setMessage({ text: 'Movimiento registrado.', tone: 'success' });
      await Promise.all([records.reload(), summary.reload()]);
    } catch (caught) { setMessage({ text: caught instanceof Error ? caught.message : 'No se pudo guardar', tone: 'error' }); }
    finally { setRecordBusy(false); }
  }

  async function saveCapital(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!capital) return;
    setSettingsBusy(true); setMessage(null);
    try {
      const saved = await endpoints.saveCapitalSettings(capital);
      setCapital(saved);
      setMessage({ text: 'Política de capital actualizada.', tone: 'success' });
      await summary.reload();
    } catch (caught) { setMessage({ text: caught instanceof Error ? caught.message : 'No se pudo guardar', tone: 'error' }); }
    finally { setSettingsBusy(false); }
  }

  function updateCapital(key: keyof CapitalSettings, value: string) {
    setCapital((current) => current ? { ...current, [key]: Number(value) } : current);
  }

  return <Page title="Costes y lotes">
    {summary.loading && !summary.data && <LoadingState />}
    {summary.error && <ErrorState error={summary.error} onRetry={() => void summary.reload()} />}
    {summary.data && <Stats>
      <Stat label="Wallet" value={currency(summary.data.walletBalance)} />
      <Stat label="Capital libre" value={currency(summary.data.capitalAvailable)} />
      <Stat label="Comprometido en industria" value={currency(summary.data.industryCommitted)} />
      <Stat label="Resultado manual · 30 días" value={currency(summary.data.net30d)} detail={`+${currency(summary.data.income30d)} / −${currency(summary.data.expenses30d)}`} tone={summary.data.net30d >= 0 ? 'pos' : 'neg'} />
    </Stats>}
    {message && <Note tone={message.tone}>{message.text}</Note>}
    <BatchAccounting />
    <div className="split split--even">
      <Section title="Límites de capital">
        {settings.loading && !settings.data && <LoadingState />}
        {settings.error && <ErrorState error={settings.error} onRetry={() => void settings.reload()} />}
        {capital && <form className="form-grid" onSubmit={saveCapital}>
          <Field label="Capital disponible (ISK)"><input min="0" step="1" value={capital.availableCapital} onChange={(event) => updateCapital('availableCapital', event.target.value)} type="number" /></Field>
          <Field label="Reserva mínima %"><input min="0" max="100" step="0.1" value={capital.reservePercent} onChange={(event) => updateCapital('reservePercent', event.target.value)} type="number" /></Field>
          <Field label="Máximo por operación (ISK)"><input min="0" step="1" value={capital.maxPerOperation} onChange={(event) => updateCapital('maxPerOperation', event.target.value)} type="number" /></Field>
          <Field label="Comisión de bróker %"><input min="0" step="0.01" value={capital.brokerFeePercent} onChange={(event) => updateCapital('brokerFeePercent', event.target.value)} type="number" /></Field>
          <Field label="Impuesto de venta %"><input min="0" step="0.01" value={capital.salesTaxPercent} onChange={(event) => updateCapital('salesTaxPercent', event.target.value)} type="number" /></Field>
          <div className="span"><Button disabled={settingsBusy}>{settingsBusy ? 'Guardando…' : 'Guardar límites'}</Button></div>
        </form>}
      </Section>
      <Section title="Añadir movimiento manual">
        <form className="form-grid" onSubmit={addRecord}>
          <Field label="Fecha"><input required name="date" type="date" /></Field>
          <Field label="Tipo"><select name="type" defaultValue="expense"><option value="expense">Gasto</option><option value="income">Ingreso</option></select></Field>
          <Field label="Importe (ISK)"><input required min="0.01" step="0.01" name="amount" type="number" /></Field>
          <Field label="Categoría"><input required maxLength={80} name="category" placeholder="Combustible, venta, tasas…" /></Field>
          <Field label="Descripción"><textarea required maxLength={500} name="description" rows={2} /></Field>
          <div className="span"><Button disabled={recordBusy}>{recordBusy ? 'Guardando…' : 'Añadir'}</Button></div>
        </form>
      </Section>
    </div>
    <Section title="Libro manual">
      {records.loading && !records.data && <LoadingState />}
      {records.error && <ErrorState error={records.error} onRetry={() => void records.reload()} />}
      {records.data && records.data.length === 0 && <EmptyState title="Sin movimientos manuales" />}
      {records.data && records.data.length > 0 && <div className="table-wrap"><table className="table--stack"><thead><tr><th>Fecha</th><th>Categoría</th><th>Descripción</th><th className="num">Importe</th></tr></thead><tbody>{records.data.map((record) => <tr key={record.id}>
        <td className="muted" data-label="Fecha" style={{ whiteSpace: 'nowrap' }}>{dateTime(record.date)}</td>
        <td data-label="Categoría">{record.category}</td>
        <td className="wide" data-label="Descripción">{record.description}</td>
        <td className={`num ${record.type === 'income' ? 'pos' : 'neg'}`} data-label="Importe">{record.type === 'income' ? '+' : '−'}{currency(record.amount)}</td>
      </tr>)}</tbody></table></div>}
    </Section>
    <Section title="Base de coste de inventario">
      {accounting.loading && !accounting.data && <LoadingState />}
      {accounting.error && <ErrorState error={accounting.error} onRetry={() => void accounting.reload()} />}
      {accounting.data && accounting.data.length === 0 && <EmptyState title="Sin base de coste" detail=" Sincroniza transacciones y activos." />}
      {accounting.data && accounting.data.length > 0 && <>
        <p className="fine">Media ponderada de compras capturadas, solo si coincide con los activos. Fabricación, botín y saldos iniciales necesitan atribución explícita. Una base desconocida no equivale a coste cero.</p>
        <div className="table-wrap"><table style={{ minWidth: 780 }}><thead><tr><th>Producto</th><th className="num">Reconstruida</th><th className="num">En activos</th><th className="num">Coste ud.</th><th className="num">Base total</th><th>Conciliación</th><th>Actualizado</th></tr></thead><tbody>{accounting.data.map((entry) => <tr key={entry.typeId}>
          <td><strong>{entry.typeName}</strong></td>
          <td className="num">{number(entry.quantity)}</td>
          <td className="num">{number(entry.actualAssetQuantity)}</td>
          <td className="num">{currency(entry.unitCost)}</td>
          <td className="num">{currency(entry.totalCost)}</td>
          <td title={entry.method === 'weighted_average_captured_purchases' ? 'Media ponderada de compras capturadas' : 'Base desconocida o inventario sin conciliar'}><Status value={entry.reconciled ? 'conciliado' : 'pendiente'} /></td>
          <td className="muted" style={{ whiteSpace: 'nowrap' }}>{dateTime(entry.updatedAt)}</td>
        </tr>)}</tbody></table></div>
      </>}
    </Section>
    <FinanceHistory />
  </Page>;
}

function BatchAccounting() {
  const batches = useApiQuery(endpoints.batches);
  const products = useApiQuery(endpoints.opportunities);
  const inventory = useApiQuery(endpoints.inventory);
  const productOptions = products.data?.map((item) => ({ id: item.typeId, name: item.typeName })) ?? [];
  const materialOptions = [...new Map([
    ...(inventory.data?.map((item) => ({ id: item.typeId, name: item.name })) ?? []),
    ...(products.data?.flatMap((item) => item.materials.map((material) => ({ id: material.typeId, name: material.name }))) ?? []),
  ].map((item) => [item.id, item])).values()].sort((a, b) => a.name.localeCompare(b.name));
  const [materialRows, setMaterialRows] = useState([0]);
  const [selling, setSelling] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  async function create(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const element = event.currentTarget;
    const form = new FormData(element);
    setBusy(true); setMessage(null);
    try {
      await endpoints.addBatch({ name: String(form.get('name')), productTypeId: Number(form.get('productTypeId')), outputQuantity: Number(form.get('outputQuantity')), jobCost: Number(form.get('jobCost')), haulingCost: Number(form.get('haulingCost')), materialCosts: materialRows.map((row) => ({ typeId: Number(form.get(`type-${row}`)), quantity: Number(form.get(`quantity-${row}`)), totalCost: Number(form.get(`cost-${row}`)) })) });
      element.reset(); setMaterialRows([0]); setMessage({ tone: 'success', text: 'Lote registrado con base de coste explícita.' }); await batches.reload();
    } catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo guardar el lote' }); }
    finally { setBusy(false); }
  }

  async function sell(event: React.FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setMessage(null);
    try {
      await endpoints.sellBatch(id, { quantity: Number(form.get('quantity')), grossRevenue: Number(form.get('grossRevenue')), salesTax: Number(form.get('salesTax')), brokerFee: Number(form.get('brokerFee')) });
      setSelling(null); setMessage({ tone: 'success', text: 'Venta atribuida al lote; beneficio realizado actualizado.' }); await batches.reload();
    } catch (caught) { setMessage({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo registrar la venta' }); }
    finally { setBusy(false); }
  }

  return <Section title="Lotes" action={<small>El beneficio solo se realiza al atribuir ventas. No se concilia con ESI ni cambia la wallet.</small>}>
    {message && <Note tone={message.tone}>{message.text}</Note>}
    {(products.error || inventory.error) && <Note tone="error">No se pudieron cargar todos los nombres. <button className="link" onClick={() => { void products.reload(); void inventory.reload(); }}>Reintentar</button></Note>}
    {batches.loading && !batches.data && <LoadingState />}
    {batches.error && <ErrorState error={batches.error} onRetry={() => void batches.reload()} />}
    {batches.data && batches.data.length === 0 && <EmptyState title="Sin lotes" detail=" Registra uno al entregar un job de fabricación." />}
    {batches.data && batches.data.length > 0 && <div className="table-wrap"><table style={{ minWidth: 780 }}><thead><tr><th>Lote</th><th className="num">Producido</th><th className="num">Vendido</th><th className="num">Base total</th><th className="num">Coste ud.</th><th className="num">Ingreso neto</th><th className="num">Beneficio</th><th /></tr></thead><tbody>{batches.data.map((batch) => <Fragment key={batch.id}>
      <tr className={selling === batch.id ? 'is-open' : undefined}>
        <td><strong>{batch.name}</strong></td>
        <td className="num">{number(batch.outputQuantity)}</td>
        <td className="num">{number(batch.soldQuantity)}</td>
        <td className="num">{currency(batch.totalBasis)}</td>
        <td className="num">{currency(batch.unitBasis)}</td>
        <td className="num">{currency(batch.realizedRevenue)}</td>
        <td className={`num ${batch.realizedProfit < 0 ? 'neg' : batch.realizedProfit > 0 ? 'pos' : ''}`}>{currency(batch.realizedProfit)}</td>
        <td className="num">{batch.soldQuantity < batch.outputQuantity && <button type="button" className="link" onClick={() => setSelling(selling === batch.id ? null : batch.id)}>Atribuir venta</button>}</td>
      </tr>
      {selling === batch.id && <tr className="detail-row"><td colSpan={8}><form className="detail form-grid" onSubmit={(event) => void sell(event, batch.id)}>
        <Field label="Unidades vendidas"><input required name="quantity" type="number" min="1" max={batch.outputQuantity - batch.soldQuantity} /></Field>
        <Field label="Ingreso bruto (ISK)"><input required name="grossRevenue" type="number" min="0.01" step="0.01" /></Field>
        <Field label="Impuesto de venta (ISK)"><input required name="salesTax" type="number" min="0" step="0.01" /></Field>
        <Field label="Comisión de bróker (ISK)"><input required name="brokerFee" type="number" min="0" step="0.01" /></Field>
        <div className="span"><Button disabled={busy}>Registrar venta</Button></div>
      </form></td></tr>}
    </Fragment>)}</tbody></table></div>}
    <details><summary>Registrar lote producido</summary><form className="form-stack" onSubmit={create}>
      <div className="form-grid">
        <Field label="Nombre del lote"><input required name="name" maxLength={200} /></Field>
        <TypePicker label="Producto fabricado" name="productTypeId" options={productOptions} />
        <Field label="Unidades producidas"><input required name="outputQuantity" type="number" min="1" max="100000000" /></Field>
        <Field label="Coste de job (ISK)"><input required name="jobCost" type="number" min="0" step="0.01" /></Field>
        <Field label="Logística (ISK)"><input required name="haulingCost" type="number" min="0" step="0.01" /></Field>
      </div>
      <fieldset className="form-stack"><legend>Materiales consumidos</legend>
        <p className="fine">Cada material a su coste real o de oportunidad, también los propios.</p>
        {materialRows.map((row) => <div className="form-grid" key={row}>
          <TypePicker label="Material" name={`type-${row}`} options={materialOptions} />
          <Field label="Cantidad"><input required name={`quantity-${row}`} type="number" min="1" /></Field>
          <Field label="Coste total (ISK)"><input required name={`cost-${row}`} type="number" min="0.01" step="0.01" /></Field>
          {materialRows.length > 1 && <div style={{ alignSelf: 'center' }}><button type="button" className="link" onClick={() => setMaterialRows((rows) => rows.filter((id) => id !== row))}>Quitar</button></div>}
        </div>)}
      </fieldset>
      <div className="actions"><Button type="button" variant="secondary" onClick={() => setMaterialRows((rows) => [...rows, Math.max(...rows) + 1])}>Añadir material</Button><Button disabled={busy}>{busy ? 'Guardando…' : 'Guardar lote'}</Button></div>
    </form></details>
  </Section>;
}

function TypePicker(props: { label: string; name: string; options: Array<{ id: number; name: string }> }) {
  return <TypeSearch {...props} />;
}
